/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo, useCallback, memo } from "react";

// @mui material components

// Argon Dashboard 2 PRO MUI components
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import breakpoints from "assets/theme/base/breakpoints";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 PRO MUI example components
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";

// Argon Dashboard 2 PRO MUI base styles

// Contexts

// PropTypes

// Tab component - extracted for better optimization
const TabItem = memo(({ label, icon }) => (
  <Tab
    label={label}
    icon={<i className={icon} style={{ marginTop: "6px", marginRight: "8px" }} />}
  />
));

TabItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};

// Business Profile component - extracted and memoized
const BusinessProfile = memo(({ business, isLoading }) => {
  if (isLoading) {
    return (
      <Grid container spacing={3} alignItems="center" sx={{ minHeight: "80px" }}>
        <Grid item>
          <Box display="flex" alignItems="center" justifyContent="center" p={2}>
            <CircularProgress size={40} thickness={3} color="info" />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item>
        <ArgonAvatar
          src={business.logoUrl}
          alt="profile-image"
          variant="rounded"
          size="xl"
          shadow="sm"
        />
      </Grid>
      <Grid item>
        <ArgonBox height="100%" mt={0.5} lineHeight={1}>
          <ArgonTypography variant="h5" fontWeight="medium">
            {business.name}
          </ArgonTypography>
          <ArgonTypography variant="button" color="text" fontWeight="medium">
            {business.businessTagLine}
          </ArgonTypography>
        </ArgonBox>
      </Grid>
    </Grid>
  );
});

BusinessProfile.propTypes = {
  business: PropTypes.shape({
    logoUrl: PropTypes.string,
    name: PropTypes.string,
    businessTagLine: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
};

function Header({ title = "" }) {
  // States
  const [isLoading, setIsLoading] = useState(true);

  // Context
  const { session } = useBusinessContext();

  // Memoized business data to prevent unnecessary re-renders
  const business = useMemo(
    () =>
      session?.selectedBusiness || {
        logoUrl: "",
        name: "No business Selected",
        businessTagLine: "Please select a business in the dropdown menu above",
      },
    [session?.selectedBusiness]
  );

  // Handle tabs orientation change based on screen size
  useEffect(() => {
    // Set loading state with timeout to show loader
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 700);

    // Cleanup function
    return () => {
      clearTimeout(loadingTimer);
    };
  }, []);

  return (
    <ArgonBox position="relative">
      <BusinessNavbar />
      <ArgonBox height="20px" />
      <Card
        sx={{
          py: 2,
          px: 2,
          boxShadow: ({ boxShadows: { md } }) => md,
        }}
      >
        <BusinessProfile business={business} isLoading={isLoading} />
      </Card>
    </ArgonBox>
  );
}

Header.propTypes = {
  title: PropTypes.string,
};

// Apply memo to the entire component for maximum optimization
export default memo(Header);
